import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import theme from './styles/theme';
import GlobalStyles from './styles/GlobalStyles';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import HomePage from './pages/HomePage';
import ChatsPage from './pages/ChatsPage';
import ChatPage from './pages/ChatPage';
import StreamingChatPage from './pages/StreamingChatPage';
import NewChatPage from './pages/NewChatPage';
import ProfilePage from './pages/ProfilePage';
import CoinsPage from './pages/CoinsPage';
import ReportPage from './pages/ReportPage';
import ReportsListPage from './pages/ReportsListPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminRoute from './components/admin/AdminRoute';
import LoadingScreen from './components/ui/LoadingScreen';
import { CssBaseline } from '@mui/material';

// Private route component
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) return <LoadingScreen message="Verifying authentication..." />;
  
  return currentUser ? <>{children}</> : <Navigate to="/login" />;
};

// Public route component (redirects to home if already logged in)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) return <LoadingScreen message="Checking authentication status..." />;
  
  return !currentUser ? <>{children}</> : <Navigate to="/" />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={<PublicRoute><LoginPage /></PublicRoute>} 
      />
      <Route 
        path="/signup" 
        element={<PublicRoute><SignupPage /></PublicRoute>} 
      />
      
      {/* Private routes */}
      <Route 
        path="/" 
        element={<PrivateRoute><HomePage /></PrivateRoute>} 
      />
      <Route 
        path="/chats" 
        element={<PrivateRoute><ChatsPage /></PrivateRoute>} 
      />
      <Route 
        path="/chat/:chatId" 
        element={<PrivateRoute><ChatPage /></PrivateRoute>} 
      />
      <Route 
        path="/streaming-chat/:chatId" 
        element={<PrivateRoute><StreamingChatPage /></PrivateRoute>} 
      />
      <Route 
        path="/new-chat" 
        element={<PrivateRoute><NewChatPage /></PrivateRoute>}
      />
      <Route 
        path="/profile" 
        element={<PrivateRoute><ProfilePage /></PrivateRoute>} 
      />
      <Route 
        path="/coins" 
        element={<PrivateRoute><CoinsPage /></PrivateRoute>} 
      />
      <Route 
        path="/reports" 
        element={<PrivateRoute><ReportsListPage /></PrivateRoute>} 
      />
      <Route 
        path="/reports/:reportId" 
        element={<PrivateRoute><ReportPage /></PrivateRoute>} 
      />
      
      {/* Admin routes */}
      <Route 
        path="/admin" 
        element={<AdminRoute><AdminDashboardPage /></AdminRoute>} 
      />
      <Route 
        path="/admin/users/:userId" 
        element={<AdminRoute><AdminUserDetailPage /></AdminRoute>} 
      />
      <Route 
        path="/admin/analytics" 
        element={<AdminRoute><AnalyticsPage /></AdminRoute>} 
      />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
